import React, { createContext, useContext, useState, useEffect } from 'react';
import { User, UserCredential, signInWithEmailAndPassword, signOut, createUserWithEmailAndPassword, signInWithPopup, signInWithCredential, GoogleAuthProvider, OAuthProvider } from 'firebase/auth';
import { doc, getDoc, setDoc, query, where, getDocs, collection, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { User as HostlyUser } from '../types';
import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';
import { pushNotificationService } from '../services/pushNotificationService';

interface AuthContextType {
  user: User | null;
  hostlyUser: HostlyUser | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<UserCredential>;
  signUp: (email: string, password: string, userData: Partial<HostlyUser>) => Promise<void>;
  signInWithGoogle: (allowCreate?: boolean) => Promise<void>;
  signInWithApple: () => Promise<void>;
  logout: () => Promise<void>;
  updateUserProfile: (userData: Partial<HostlyUser>) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [hostlyUser, setHostlyUser] = useState<HostlyUser | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      setUser(firebaseUser);

      try {
        if (firebaseUser) {
          try {
            const userDoc = await getDoc(doc(db, 'users', firebaseUser.uid));

            if (userDoc.exists()) {
              setHostlyUser(userDoc.data() as HostlyUser);

              // Sincronizar subscription com Stripe silenciosamente no background
              try {
                const { stripeApi } = await import('../services/stripeApiService');
                await stripeApi.getSubscriptionStatus(firebaseUser.uid);
              } catch (syncError) {
                console.warn('Falha na sincronização de subscription (não crítico):', syncError);
              }
            } else {
              const newUser: any = {
                uid: firebaseUser.uid,
                name: firebaseUser.displayName?.split(' ')[0] || 'Usuário',
                lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
                email: (firebaseUser.email || '').trim().toLowerCase(),
                createdAt: new Date(),
              };

              if (firebaseUser.photoURL) {
                newUser.profilePictureUrl = firebaseUser.photoURL;
              }

              await setDoc(doc(db, 'users', firebaseUser.uid), newUser);

              const subscriptionRef = doc(db, 'users', firebaseUser.uid, 'subscription', 'current');
              await setDoc(subscriptionRef, {
                plan: 'free',
                status: 'active',
                billingInterval: 'monthly',
                createdAt: new Date(),
                updatedAt: new Date(),
              });

              setHostlyUser(newUser as HostlyUser);
            }

            // Inicializar push notifications após login bem-sucedido
            await pushNotificationService.initialize({
              userId: firebaseUser.uid,
              onNotificationReceived: (notification) => {
                console.log('📩 Notificação recebida no app:', notification);
                // Aqui você pode mostrar um toast ou dialog customizado
              },
              onNotificationAction: (action) => {
                console.log('👆 Usuário clicou na notificação:', action);
                // Aqui você pode navegar para a tela correspondente
                const notificationData = (action.notification.data || {}) as Record<string, string>;

                if (notificationData?.postId) {
                  window.location.href = `/hostlyclub/post/${notificationData.postId}`;
                } else if (notificationData?.eventId) {
                  window.location.href = `/events/${notificationData.eventId}`;
                } else if (notificationData?.guestId) {
                  window.location.href = `/guests/${notificationData.guestId}`;
                } else if (notificationData?.type === 'new_follower' && notificationData?.senderId) {
                  window.location.href = `/hostlyclub/profile/${notificationData.senderId}`;
                } else if (notificationData?.type === 'post_like' || notificationData?.type === 'post_comment') {
                  window.location.href = '/hostly-club';
                } else {
                  window.location.href = '/notifications';
                }
              }
            });

          } catch (error: any) {
            console.error('Erro ao carregar usuário do Firestore:', error);
            const tempUser: HostlyUser = {
              uid: firebaseUser.uid,
              name: firebaseUser.displayName?.split(' ')[0] || 'Usuário',
              lastName: firebaseUser.displayName?.split(' ').slice(1).join(' ') || '',
              email: (firebaseUser.email || '').trim().toLowerCase(),
              profilePictureUrl: firebaseUser.photoURL || undefined,
            };
            setHostlyUser(tempUser);
          }
        } else {
          setHostlyUser(null);
          // Cleanup push notifications no logout
          await pushNotificationService.updateUserId(null);
        }
      } catch (error) {
        console.error('Erro inesperado no onAuthStateChanged:', error);
        setHostlyUser(null);
      } finally {
        setLoading(false);
      }
    });

    timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    return () => {
      clearTimeout(timeout);
      unsubscribe();
    };
  }, []);

  const checkEmailExists = async (email: string) => {
    try {
      const q = query(collection(db, 'users'), where('email', '==', email.trim().toLowerCase()));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error: any) {
      return false;
    }
  };

  const checkPhoneExists = async (phone: string) => {
    try {
      const q = query(collection(db, 'users'), where('phone', '==', phone));
      const querySnapshot = await getDocs(q);
      return !querySnapshot.empty;
    } catch (error: any) {
      return false;
    }
  };

  const signIn = async (email: string, password: string): Promise<UserCredential> => {
    try {
      const credential = await signInWithEmailAndPassword(auth, email, password);
      return credential;
    } catch (error: any) {
      if (![
        'auth/invalid-email',
        'auth/user-not-found',
        'auth/wrong-password',
        'auth/invalid-credential'
      ].includes(error?.code)) {
        console.warn('Erro inesperado no signIn (AuthContext):', error);
      }
      throw error;
    }
  };

  const signUp = async (email: string, password: string, userData: Partial<HostlyUser> = {}) => {
    setLoading(true);
    try {
      if (await checkEmailExists(email)) {
        throw new Error('email-already-in-use');
      }

      if (userData.phone && (await checkPhoneExists(userData.phone))) {
        const error = new Error('phone-already-in-use') as any;
        error.code = 'phone-already-in-use';
        throw error;
      }

      const { user: newFirebaseUser } = await createUserWithEmailAndPassword(auth, email, password);

      const newUser: HostlyUser = {
        uid: newFirebaseUser.uid,
        name: userData.name || 'Usuário',
        lastName: userData.lastName || '',
        email: email.trim().toLowerCase(),
        phone: userData.phone || '',
        gender: userData.gender || '',
        ...userData
      };

      if (userData.profilePictureUrl) {
        newUser.profilePictureUrl = userData.profilePictureUrl;
      }

      await setDoc(doc(db, 'users', newFirebaseUser.uid), {
        ...newUser,
        createdAt: new Date(),
      });

      const subscriptionRef = doc(db, 'users', newFirebaseUser.uid, 'subscription', 'current');
      await setDoc(subscriptionRef, {
        plan: 'free',
        status: 'active',
        billingInterval: 'monthly',
        createdAt: new Date(),
        updatedAt: new Date(),
      });

    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithGoogle = async () => {
    setLoading(true);
    try {
      console.log('[AUTH] Iniciando Google Sign-In...');
      // Usar autenticação nativa no mobile, web no browser
      if (Capacitor.isNativePlatform()) {
        console.log('[AUTH] Plataforma nativa detectada');
        // Autenticação nativa para iOS e Android
        const result = await FirebaseAuthentication.signInWithGoogle();
        console.log('[AUTH] Resultado do plugin:', result);

        if (!result || !result.credential) {
          throw new Error('Falha na autenticação Google');
        }

        console.log('[AUTH] Criando credencial Firebase...');
        // Criar credencial do Firebase com o token do Google
        const credential = GoogleAuthProvider.credential(result.credential.idToken);
        const userCredential = await signInWithCredential(auth, credential);

        if (!userCredential || !userCredential.user) {
          throw new Error('Falha ao autenticar no Firebase');
        }

        console.log('[AUTH] Usuário autenticado:', userCredential.user.uid);
        console.log('[AUTH] Login concluído com sucesso!');
      } else {
        console.log('[AUTH] Plataforma web detectada');
        // Autenticação web (popup)
        const provider = new GoogleAuthProvider();
        const result = await signInWithPopup(auth, provider);

        if (!result || !result.user) {
          throw new Error('Falha na autenticação Google');
        }

        console.log('[AUTH] Login web concluído com sucesso!');
      }
    } catch (error: any) {
      console.error('[AUTH] Erro no Google Sign-In:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const signInWithApple = async () => {
    setLoading(true);
    try {
      console.log('[AUTH] Iniciando Apple Sign-In...');

      if (Capacitor.isNativePlatform()) {
        console.log('[AUTH] Plataforma nativa detectada');

        // Timeout de 30 segundos
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error('Timeout: A autenticação demorou muito')), 30000)
        );

        // Chamar o plugin com skipNativeAuth: true
        // O plugin gera o nonce internamente e retorna no credential.nonce
        const result = await Promise.race([
          FirebaseAuthentication.signInWithApple({
            skipNativeAuth: true,
            scopes: ['email', 'name'],
          }),
          timeoutPromise
        ]) as any;

        console.log('[AUTH] Resultado do plugin Apple:', JSON.stringify(result));

        if (!result || !result.credential || !result.credential.idToken) {
          throw new Error('Falha na autenticação Apple - token não recebido');
        }

        // O plugin retorna o rawNonce em credential.nonce
        // Usar esse nonce para criar a credencial do Firebase
        const pluginRawNonce = result.credential.nonce;
        console.log('[AUTH] rawNonce do plugin:', pluginRawNonce);

        if (!pluginRawNonce) {
          throw new Error('Nonce não retornado pelo plugin');
        }

        // Criar credencial do Firebase com o nonce retornado pelo plugin
        console.log('[AUTH] Criando credencial Firebase...');
        const provider = new OAuthProvider('apple.com');
        const credential = provider.credential({
          idToken: result.credential.idToken,
          rawNonce: pluginRawNonce, // Usar nonce do plugin
        });

        // Autenticar no Firebase
        const userCredential = await signInWithCredential(auth, credential);

        if (!userCredential || !userCredential.user) {
          throw new Error('Falha ao autenticar no Firebase');
        }

        console.log('[AUTH] Usuário autenticado:', userCredential.user.uid);
        console.log('[AUTH] Apple Sign-In concluído com sucesso!');

      } else {
        console.log('[AUTH] Plataforma web detectada');
        // Autenticação web (popup)
        const provider = new OAuthProvider('apple.com');
        provider.addScope('email');
        provider.addScope('name');

        const result = await signInWithPopup(auth, provider);

        if (!result || !result.user) {
          throw new Error('Falha na autenticação Apple');
        }

        console.log('[AUTH] Login web concluído com sucesso!');
      }
    } catch (error: any) {
      console.error('[AUTH] Erro no Apple Sign-In:', error);

      // Tratamento de erros específicos
      if (error.message?.includes('1001')) {
        throw new Error('Autenticação cancelada pelo usuário');
      } else if (error.message?.includes('Timeout')) {
        throw new Error('A autenticação demorou muito. Tente novamente.');
      } else if (error.code === 'auth/missing-or-invalid-nonce') {
        throw new Error('Erro de autenticação Apple. Tente novamente.');
      }

      throw error;
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      // Cleanup push notifications antes de deslogar
      await pushNotificationService.updateUserId(null);
      await signOut(auth);
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const updateUserProfile = async (userData: Partial<HostlyUser>) => {
    if (!user?.uid) throw new Error('Usuário não autenticado');

    try {
      const userRef = doc(db, 'users', user.uid);
      const payload: any = {
        ...userData,
        updatedAt: new Date().toISOString(),
      };
      if (Object.prototype.hasOwnProperty.call(userData, 'phone')) {
        payload.phoneNormalized = String((userData as any).phone || '').replace(/\D/g, '');
      }
      await updateDoc(userRef, payload);

      // Atualizar estado local do hostlyUser
      setHostlyUser(prev => prev ? { ...prev, ...userData } : null);
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      throw error;
    }
  };

  const deleteAccount = async () => {
    if (!user?.uid) throw new Error('Usuário não autenticado');

    setLoading(true);
    try {
      // Importar dinamicamente para evitar carregar se não for usado
      const { httpsCallable } = await import('firebase/functions');
      const { functions } = await import('../config/firebase');

      // Chamar Cloud Function para deletar conta (bypasses requires-recent-login)
      const deleteAccountFunction = httpsCallable(functions, 'deleteAccount');
      await deleteAccountFunction();

      // Deslogar localmente
      await signOut(auth);

      setUser(null);
      setHostlyUser(null);
    } catch (error: any) {
      console.error('Erro ao deletar conta:', error);
      throw new Error(error.message || 'Erro ao deletar conta. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const value = {
    user,
    hostlyUser,
    loading,
    signIn,
    signUp,
    signInWithGoogle,
    signInWithApple,
    logout,
    updateUserProfile,
    deleteAccount,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
